import { z } from 'zod';

import { TABLAS_QUE_BAJAN } from './sincronizacion';

/**
 * Comprobar que los dos lados dicen lo mismo.
 *
 * ## Por qué hace falta algo aparte de la sincronización
 *
 * Porque la sincronización transporta **cambios**, no estado. Cada fila que se
 * toca deja una marca y la marca viaja; pero una diferencia que nadie tocó no
 * genera marca, y entonces no se corrige nunca.
 *
 * No es teórico. Un residente llevaba `recibeAvisos` en `true` arriba y `false`
 * abajo **desde la siembra**, sin ninguna marca pendiente y con el enlace
 * perfectamente sano. El síntoma fue que no le llegó un aviso; encontrarlo
 * costó horas y solo se pudo porque alguien estaba esperando esa notificación.
 * En un fraccionamiento con gente de verdad, nadie lo habría notado.
 *
 * ## Dos fases, porque la barata basta casi siempre
 *
 * Primero una huella por tabla: un número por lado. Si coinciden —que es lo
 * normal— ahí acaba, y ha costado un recuento. Solo cuando no coinciden se
 * comparan las filas una por una para saber **cuáles**.
 */

/**
 * Columnas que no cuentan para la huella.
 *
 * Las mismas que el disparador `kodica_marcar` ignora, y por el mismo motivo:
 * son mecánica, no contenido. `ultimoLatido` lo escribe el MQTT de la caseta
 * cada pocos segundos y arriba llega con retraso, así que compararlo daría
 * diferencia siempre; `subidaEn` solo significa algo abajo.
 *
 * `actualizadoEn` va por una razón propia y se descubrió a la primera pasada:
 * lo escribe Prisma en **la máquina que hace la escritura**, no viaja copiado.
 * En una fila que solo se replica coincide, pero `Dispositivo` lo escriben los
 * dos lados por su cuenta —la caseta con el latido de MQTT, la nube al recibir
 * el reporte— y difería por milisegundos. El cotejo declaraba distintos los
 * nueve aparatos y los mandaba a bajar, con lo que la nube le devolvía a la
 * caseta un `ultimoLatido` más viejo que el suyo: exactamente el rebote que
 * `no_marcar_latidos` había venido a quitar.
 *
 * No es contenido: es contabilidad de la escritura. Quitarlo no afloja la
 * comprobación, la afila.
 *
 * Si esta lista y la del disparador se separan, el cotejo empieza a inventarse
 * diferencias que no lo son — y una alarma que suena sin motivo se acaba
 * ignorando, que es peor que no tenerla.
 */
export const COLUMNAS_DE_MECANICA = ['ultimoLatido', 'subidaEn', 'actualizadoEn'] as const;

/**
 * Tablas que **suben** y también se cotean.
 *
 * `Acceso` es el registro de quién entró y la nube lo guarda como respaldo. Sin
 * cotejarlo, una nube restaurada de una copia de hace dos días **nunca vuelve a
 * recibir** los accesos de esas dos horas: sus marcas ya se cerraron abajo, y
 * la sincronización transporta cambios, no estado. El respaldo quedaría con un
 * agujero permanente y nada lo diría.
 *
 * `Foto` no está aquí a propósito: la nube guarda la ficha pero borra la imagen
 * a los siete días, así que sus filas divergen por diseño.
 */
export const TABLAS_QUE_SUBEN_Y_SE_COTEJAN = ['Acceso'] as const;

/** Lo que se compara, venga de donde venga. */
export const TABLAS_QUE_SE_COTEJAN = [
  ...TABLAS_QUE_BAJAN,
  ...TABLAS_QUE_SUBEN_Y_SE_COTEJAN,
] as const;

/**
 * De qué lado está la verdad de una tabla. Es lo que decide **quién repara**.
 *
 * En el padrón manda la nube: si difieren, ella remarca y la fila baja. En los
 * accesos manda la caseta: si difieren, la caseta remarca y la fila sube.
 *
 * Y en las que suben hay una regla que no se puede relajar: **el cotejo solo
 * añade lo que a la nube le falta, nunca le quita.** Hoy la caseta no purga
 * accesos —solo fotos, por `retencionFotosDias`— así que una comparación
 * simétrica no haría daño. El día que alguien añada retención de accesos, esa
 * comparación vería que abajo hay menos y le pediría a la nube que borrara el
 * respaldo: justo lo contrario de para lo que existe. Una caseta que perdió
 * datos no puede tener la facultad de decirle a la nube que los olvide.
 */
export function laVerdadEstaAbajo(tabla: string): boolean {
  return (TABLAS_QUE_SUBEN_Y_SE_COTEJAN as readonly string[]).includes(tabla);
}

/**
 * Cuántos días de accesos se cotean.
 *
 * No todos: una caseta no purga accesos, así que a los dos años son cientos de
 * miles de filas comparadas cada seis horas para encontrar cero diferencias.
 * Treinta días son holgados frente a lo que esto persigue —una nube restaurada
 * de una copia— porque las copias son diarias y el agujero se mide en horas.
 *
 * **Lo que esto NO cubre**, y conviene saberlo: una nube restaurada de una
 * copia de hace más de un mes no recupera los accesos anteriores a la ventana.
 * Si algún día pasa, se sube esta constante a mano y se espera una vuelta.
 */
export const COTEJO_ACCESOS_DIAS = 30;

export const huellaTablaSchema = z.object({
  tabla: z.string().min(1).max(64),
  filas: z.number().int().min(0),
  /** md5 de las filas de esa tabla, en orden de `id`. */
  huella: z.string().length(32),
});

export type HuellaTabla = z.infer<typeof huellaTablaSchema>;

/**
 * Fase 1: la caseta pide las huellas de la nube y compara ella.
 *
 * Al revés —mandar las suyas y que compare la nube— costaría lo mismo y una
 * petición más. Y la comparación conviene que la haga quien tiene el interés:
 * es la caseta la que necesita enterarse de que le falta padrón.
 */
export const pedirCotejoSchema = z.object({
  condominio: z.string().min(1).max(60),
});

export type PedirCotejo = z.infer<typeof pedirCotejoSchema>;

/** Lo que contesta la fase 1. */
export interface HuellasDeLaNube {
  huellas: HuellaTabla[];
}

/** Fase 2: para una tabla que no cuadró, la huella de cada fila. */
export const cotejoFilasSchema = z.object({
  condominio: z.string().min(1).max(60),
  tabla: z.string().min(1).max(64),
  filas: z
    .array(z.object({ id: z.string().min(1).max(64), huella: z.string().length(32) }))
    .max(20_000),
});

export type CotejoFilas = z.infer<typeof cotejoFilasSchema>;

/**
 * Lo que la nube contesta sobre una tabla.
 *
 * **Las tres listas son cosas distintas y no se tratan igual.** `distintas` son
 * filas que existen en los dos lados con el mismo `id` y otro contenido: eso es
 * el fallo, y la nube manda a bajar las suyas. `soloArriba` viajará sola en
 * cuanto se marque. `soloAbajo` **no se toca**: la cuenta de servicio del
 * kiosco vive solo en la caseta y una reconciliación que «iguale» los dos lados
 * la borraría, dejando la reja sin con qué operar.
 */
export const diferenciasSchema = z.object({
  tabla: z.string(),
  distintas: z.array(z.string()),
  soloArriba: z.array(z.string()),
  soloAbajo: z.array(z.string()),
  /** Cuántas filas se mandaron a bajar de nuevo. */
  remarcadas: z.number().int().min(0),
});

export type Diferencias = z.infer<typeof diferenciasSchema>;

/** Cada cuánto coteja la caseta. Es caro comparado con lo demás y no urge. */
export const COTEJO_INTERVALO_MS = 6 * 60 * 60_000;
